import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../../../../../redux";
import { axiosRestInstance } from "../../../../../../../common/axios";

/**
 * Resolves a timeline "submitted_by" user id to a display name.
 *
 * The loaded redux lists only cover HID-type users (userListHID) and the current
 * partner's users (userList). Submitters such as Super Admin / data-entry users are
 * in neither list, so for any id not found locally we fetch it once via /user/getUser
 * and cache the result. Falls back to the raw id only if the user can't be found.
 */
export function useResolveSubmittedBy(
  ids: Array<number | null | undefined>
): (id?: number | null) => string {
  const { userListHID, userList } = useAppSelector((state) => state.user);
  const [fetchedNames, setFetchedNames] = useState<Record<number, string>>({});
  const inFlightRef = useRef<Set<number>>(new Set());

  const findInLists = useCallback(
    (id: number): string | undefined => {
      const found =
        userListHID?.find((u) => u.ID === id) ||
        userList?.find((u) => u.ID === id);
      return found
        ? `${found.first_name ?? ""} ${found.last_name ?? ""}`.trim()
        : undefined;
    },
    [userListHID, userList]
  );

  const idsKey = Array.from(
    new Set(
      ids.filter((id): id is number => typeof id === "number" && id > 0)
    )
  )
    .sort((a, b) => a - b)
    .join(",");

  useEffect(() => {
    if (!idsKey) return;
    const uniqueIds = idsKey.split(",").map(Number);

    uniqueIds.forEach((id) => {
      if (findInLists(id)) return;
      if (fetchedNames[id] !== undefined) return;
      if (inFlightRef.current.has(id)) return;

      inFlightRef.current.add(id);
      axiosRestInstance
        .get(`/user/getUser?ID=${id}`)
        .then((res) => {
          const data = res?.data?.data;
          const name = data
            ? `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim()
            : "";
          setFetchedNames((prev) => ({ ...prev, [id]: name || String(id) }));
        })
        .catch(() => {
          setFetchedNames((prev) => ({ ...prev, [id]: String(id) }));
        })
        .finally(() => {
          inFlightRef.current.delete(id);
        });
    });
  }, [idsKey, findInLists, fetchedNames]);

  return useCallback(
    (id?: number | null): string => {
      if (!id) return "-";
      const fromList = findInLists(id);
      if (fromList) return fromList;
      const fromFetch = fetchedNames[id];
      if (fromFetch) return fromFetch;
      return String(id);
    },
    [findInLists, fetchedNames]
  );
}
